import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Bell, Plus, Trash2, ToggleLeft, ToggleRight, AlertCircle, TrendingUp, Calendar, Shield, Zap, CheckCircle, Activity, LineChart, Waves, BarChart3, Sparkles } from 'lucide-react';
import { useAlertStore } from '../../../state/alertStore';
import { useSignalStore } from '../../../state/signalStore';
import { useAuthStore } from '../../../state/authStore';
import SectionNav from '../../../components/layout/SectionNav';
import { portfolioTabsFor } from '../../../nav/publicNav';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const ALERT_TYPE_INFO = {
  FII_THRESHOLD: { icon: TrendingUp, label: 'FII Holding Threshold', color: 'text-blue-500', description: 'Alert when FII holding crosses a percentage', group: 'Flow' },
  DII_THRESHOLD: { icon: TrendingUp, label: 'DII Holding Threshold', color: 'text-indigo-500', description: 'Alert when DII holding crosses a percentage', group: 'Flow' },
  INSIDER_BUY: { icon: TrendingUp, label: 'Insider Buy', color: 'text-emerald-500', description: 'Alert when company insiders buy shares', group: 'Flow' },
  INSIDER_SELL: { icon: AlertCircle, label: 'Insider Sell', color: 'text-red-500', description: 'Alert when company insiders sell shares', group: 'Flow' },
  EARNINGS_DATE: { icon: Calendar, label: 'Earnings Date', color: 'text-amber-500', description: 'Alert before quarterly earnings announcements', group: 'Corporate' },
  CORPORATE_ACTION: { icon: Bell, label: 'Corporate Action', color: 'text-purple-500', description: 'Dividends, splits, bonus issues', group: 'Corporate' },
  SCORE_CHANGE: { icon: Shield, label: 'Score Change', color: 'text-emerald-500', description: 'Alert when AI stock score changes', group: 'Fundamental' },
  PROMOTER_PLEDGE: { icon: AlertCircle, label: 'Promoter Pledge', color: 'text-red-500', description: 'Alert when promoters pledge shares', group: 'Fundamental' },
  // ── Technical analysis ──
  PRICE_CROSS: { icon: LineChart, label: 'Price Cross', color: 'text-sky-500', description: 'Close crosses a price level', group: 'Technical' },
  MA_CROSS: { icon: LineChart, label: 'MA Cross', color: 'text-sky-500', description: 'Fast MA crosses slow MA (golden / death cross)', group: 'Technical' },
  RSI_THRESHOLD: { icon: Waves, label: 'RSI Threshold', color: 'text-violet-500', description: 'RSI(14) crosses a threshold (e.g. 70 / 30)', group: 'Technical' },
  MACD_CROSS: { icon: Waves, label: 'MACD Cross', color: 'text-emerald-500', description: 'MACD line crosses its signal line', group: 'Technical' },
  VOLUME_SPIKE: { icon: BarChart3, label: 'Volume Spike', color: 'text-amber-500', description: "Today's volume exceeds multiplier of 20-day average", group: 'Technical' },
  BREAKOUT: { icon: Sparkles, label: 'Breakout', color: 'text-emerald-500', description: 'Close breaks above / below N-day range', group: 'Technical' },
  PATTERN_DETECTED: { icon: Activity, label: 'TA Pattern Detected', color: 'text-fuchsia-500', description: 'One of the named chart patterns just fired', group: 'Technical' },
};

const CHANNELS = [
  { key: 'in_app', label: 'In-App' },
  { key: 'email', label: 'Email' },
  { key: 'push', label: 'Push' },
];

export default function AlertRulesPage() {
  const navigate = useNavigate();
  const { rules, events, loading, error, fetchRules, fetchEvents, createRule, toggleRule, deleteRule, markRead, markAllRead } = useAlertStore();
  const { signals, fetchSignals } = useSignalStore();
  const [showCreate, setShowCreate] = useState(false);
  const [alertTypes, setAlertTypes] = useState([]);
  const [form, setForm] = useState({ alert_type: 'FII_THRESHOLD', symbol: '', channels: ['in_app'], params: {} });
  const [activeSection, setActiveSection] = useState('rules');
  const userType = useAuthStore(s => s.user?.userType);
  const subNavTabs = portfolioTabsFor(userType);

  useEffect(() => {
    fetchRules();
    fetchEvents();
    fetchSignals(20);
    fetch(`${API}/alerts/types`).then(r => r.json()).then(d => setAlertTypes(d.types || [])).catch(() => {});
  }, [fetchRules, fetchEvents, fetchSignals]);

  const aiSignals = (signals || []).filter(s => s.source === 'ai_signal').slice(0, 10);

  const handleCreate = async (e) => {
    e.preventDefault();
    const success = await createRule({
      alert_type: form.alert_type,
      symbol: form.symbol || null,
      channels: form.channels,
      params: form.params,
    });
    if (success) {
      setShowCreate(false);
      setForm({ alert_type: 'FII_THRESHOLD', symbol: '', channels: ['in_app'], params: {} });
    }
  };

  return (
    <div className="space-y-4">
      {subNavTabs.length > 1 && <SectionNav tabs={subNavTabs} />}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-500" /> Alert Rules
          </h1>
          <p className="text-xs text-muted-foreground">Configure automated alerts for stock events</p>
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          onClick={() => setShowCreate(!showCreate)}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Create Rule
        </Button>
      </div>

      {showCreate && (
        <Card className="border-emerald-500/30">
          <CardHeader className="pb-2"><CardTitle className="text-sm">New Alert Rule</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Alert Type</label>
                <select value={form.alert_type} onChange={e => setForm({ ...form, alert_type: e.target.value })}
                  className="w-full h-8 px-2 text-xs rounded-md border border-border bg-background">
                  {Object.entries(ALERT_TYPE_INFO).map(([key, info]) => (
                    <option key={key} value={key}>{info.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Stock Symbol (optional — blank = all stocks)</label>
                <input value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                  placeholder="e.g. RELIANCE"
                  className="w-full h-8 px-2 text-xs rounded-md border border-border bg-background" />
              </div>
              <ParamsFormForType
                alertType={form.alert_type}
                params={form.params}
                onChange={(p) => setForm({ ...form, params: p })}
              />
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Delivery Channels</label>
                <div className="flex gap-2">
                  {CHANNELS.map(ch => (
                    <label key={ch.key} className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={form.channels.includes(ch.key)}
                        onChange={e => {
                          const channels = e.target.checked
                            ? [...form.channels, ch.key]
                            : form.channels.filter(c => c !== ch.key);
                          setForm({ ...form, channels });
                        }} />
                      {ch.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                  Create
                </Button>
                <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Section Tabs */}
      <div className="flex gap-1 p-1 bg-card/50 rounded-lg w-fit">
        {[
          { id: 'rules', label: 'Rules', icon: Bell },
          { id: 'events', label: 'Recent Events', icon: Activity, count: events.filter(e => !e.is_read).length },
          { id: 'signals', label: 'Smart Signals', icon: Zap, count: aiSignals.length },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveSection(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeSection === t.id ? 'bg-emerald-500/10 text-emerald-500' : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
            {t.count > 0 && <Badge className="text-[9px] bg-red-500/10 text-red-500 ml-1">{t.count}</Badge>}
          </button>
        ))}
      </div>

      {/* Rules Section */}
      {activeSection === 'rules' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No alert rules configured yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Create your first rule to get automated notifications.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => {
                const info = ALERT_TYPE_INFO[rule.alert_type] || {};
                const Icon = info.icon || Bell;
                return (
                  <Card key={rule.id} className={`transition-all ${!rule.is_enabled ? 'opacity-50' : ''}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <Icon className={`w-5 h-5 shrink-0 ${info.color || 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{info.label || rule.alert_type}</span>
                          {rule.symbol && <Badge variant="secondary" className="text-[10px]">{rule.symbol}</Badge>}
                          {rule.company_name && <span className="text-xs text-muted-foreground">{rule.company_name}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                          <span>Channels: {(rule.channels || []).join(', ')}</span>
                          {rule.params?.threshold && <span>Threshold: {rule.params.threshold}%</span>}
                        </div>
                      </div>
                      <button onClick={() => toggleRule(rule.id, !rule.is_enabled)}
                        className="p-1 hover:bg-muted rounded transition-colors">
                        {rule.is_enabled ?
                          <ToggleRight className="w-6 h-6 text-emerald-500" /> :
                          <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                      </button>
                      <button onClick={() => deleteRule(rule.id)}
                        className="p-1 hover:bg-red-50 dark:hover:bg-red-950/20 rounded text-muted-foreground hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Events Section */}
      {activeSection === 'events' && (
        <div className="space-y-2">
          {events.length > 0 && (
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" className="text-xs" onClick={markAllRead}>
                <CheckCircle className="w-3 h-3 mr-1" />Mark all read
              </Button>
            </div>
          )}
          {events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No alert events yet. Events appear when your rules fire.</p>
            </div>
          ) : events.map(ev => (
            <Card key={ev.id} className={`transition-all ${ev.is_read ? 'opacity-60' : 'border-emerald-500/20'}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${ev.is_read ? 'bg-gray-400' : 'bg-emerald-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{ev.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{ev.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-[9px] ${
                        ev.severity === 'HIGH' ? 'bg-red-500/10 text-red-500' :
                        ev.severity === 'MEDIUM' ? 'bg-amber-500/10 text-amber-600' : 'bg-blue-500/10 text-blue-500'
                      }`}>{ev.severity}</Badge>
                      {ev.triggered_at && <span className="text-[10px] text-muted-foreground">{new Date(ev.triggered_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  {!ev.is_read && (
                    <button onClick={() => markRead(ev.id)} className="text-xs text-muted-foreground hover:text-foreground">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Smart Signals Section */}
      {activeSection === 'signals' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">AI-detected patterns from market data</p>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/public/signals')}>
              View All Signals →
            </Button>
          </div>
          {aiSignals.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Zap className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No AI signals detected recently.</p>
            </div>
          ) : aiSignals.map(sig => (
            <Card key={sig.id} className="hover:border-emerald-500/20 transition-colors">
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <Zap className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{sig.title}</p>
                    {sig.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{sig.message}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {sig.symbol && (
                        <button onClick={() => navigate(`/public/india-company/${sig.symbol}`)}
                          className="text-[10px] text-emerald-500 hover:underline">{sig.symbol}</button>
                      )}
                      <Badge className={`text-[9px] ${
                        sig.severity === 'HIGH' ? 'bg-red-500/10 text-red-500' :
                        sig.severity === 'MEDIUM' ? 'bg-amber-500/10 text-amber-600' : 'bg-blue-500/10 text-blue-500'
                      }`}>{sig.severity}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Per-alert-type parameter form ─────────────────────────

const PATTERN_OPTIONS = [
  'golden_cross', 'death_cross', 'macd_bull_cross', 'macd_bear_cross',
  'double_bottom', 'double_top', 'head_shoulders', 'inverse_head_shoulders',
  'bullish_engulfing', 'bearish_engulfing',
];

function ParamsFormForType({ alertType, params, onChange }) {
  const p = params || {};
  const set = (k, v) => onChange({ ...p, [k]: v });

  const numField = (key, label, step = '0.1', placeholder = '') => (
    <div className="col-span-1">
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <input
        type="number" step={step}
        value={p[key] ?? ''}
        onChange={(e) => set(key, parseFloat(e.target.value) || 0)}
        placeholder={placeholder}
        className="w-full h-8 px-2 text-xs rounded-md border border-border bg-background"
      />
    </div>
  );
  const selectField = (key, label, options, dflt = '') => (
    <div className="col-span-1">
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <select
        value={p[key] || dflt}
        onChange={(e) => set(key, e.target.value)}
        className="w-full h-8 px-2 text-xs rounded-md border border-border bg-background"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  if (alertType === 'FII_THRESHOLD' || alertType === 'DII_THRESHOLD') {
    return <div className="grid grid-cols-2 gap-2">{numField('threshold', 'Min change (pp)', '0.1', 'e.g. 5.0')}</div>;
  }
  if (alertType === 'SCORE_CHANGE') {
    return <div className="grid grid-cols-2 gap-2">{numField('threshold', 'Min % move', '1', 'e.g. 20')}</div>;
  }
  if (alertType === 'PRICE_CROSS') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {numField('level', 'Price ₹', '0.05', 'e.g. 1450')}
        {selectField('direction', 'Direction', ['above', 'below'], 'above')}
      </div>
    );
  }
  if (alertType === 'MA_CROSS') {
    return (
      <div className="grid grid-cols-3 gap-2">
        {numField('fast', 'Fast MA', '1', '50')}
        {numField('slow', 'Slow MA', '1', '200')}
        {selectField('direction', 'Direction', ['bull', 'bear'], 'bull')}
      </div>
    );
  }
  if (alertType === 'RSI_THRESHOLD') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {numField('threshold', 'RSI level', '1', '70')}
        {selectField('direction', 'Direction', ['above', 'below'], 'above')}
      </div>
    );
  }
  if (alertType === 'MACD_CROSS') {
    return <div className="grid grid-cols-2 gap-2">{selectField('direction', 'Direction', ['bull', 'bear'], 'bull')}</div>;
  }
  if (alertType === 'VOLUME_SPIKE') {
    return <div className="grid grid-cols-2 gap-2">{numField('multiplier', 'Multiplier × 20d avg', '0.1', '2.0')}</div>;
  }
  if (alertType === 'BREAKOUT') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {numField('lookback_days', 'Lookback days', '1', '20')}
        {selectField('direction', 'Direction', ['bull', 'bear'], 'bull')}
      </div>
    );
  }
  if (alertType === 'PATTERN_DETECTED') {
    const selected = new Set(p.signal_types || []);
    return (
      <div>
        <label className="text-xs text-muted-foreground block mb-1">Patterns to watch for</label>
        <div className="flex flex-wrap gap-1.5">
          {PATTERN_OPTIONS.map((opt) => {
            const on = selected.has(opt);
            return (
              <button
                type="button"
                key={opt}
                onClick={() => {
                  const next = new Set(selected);
                  if (on) next.delete(opt); else next.add(opt);
                  set('signal_types', Array.from(next));
                }}
                className={`px-2 py-0.5 rounded-md text-[11px] border ${
                  on
                    ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-500'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
}
